import React, { useEffect, useState } from "react";
import { useCopy } from "../../utils/globalContext";
import {
  minAsyncDelay,
  MINIMUM_SPINNER_DELAY_MS,
} from "../../utils/minAsyncDelay";
import { useAppDispatch } from "../../store/configureStore";
import {
  clearErrorCopyKey,
  getCompletionOTPState,
  requestChangeEmail,
  requestChangePhone,
  setCompletionOTPState,
} from "../../store/auth";
import type { RootState } from "../../store/rootReducer";
import type { OTPState } from "../../utils/use-completions";
import { useSelector } from "react-redux";
import {
  Body,
  Button,
  Dialog,
  Flex,
  Heading,
  Loader,
  Margin,
  OtpInput,
  RoundedBackground,
  styled,
  useIsSmallScreenSize,
  useTheme,
} from "@kivra/react-components";
import {
  CheckmarkCircleFilledIcon,
  QuestionIcon,
} from "@kivra/react-components/icons";
import type { BECompletionV4 } from "@kivra/sdk/types/core/completion";
import { Card } from "./Card";

interface OtpScreen {
  onSuccess(): void;
  requestEmailOtp(email: string): Promise<unknown>;
  requestPhoneOtp(phone: string): Promise<unknown>;
  onAbort(): Promise<void> | void;
  completion: BECompletionV4;
  /** The value of phone or email that the user has inputted. */
  value: string;
}

/**
 * The Otp Screen is responsible for displaying a six digit input field for
 * Otp codes. When filled, the Otp gets sent to the BE to be validated.
 */
export function OtpScreen(props: OtpScreen) {
  switch (props.completion.type) {
    case "email":
      return <OtpEmailScreen {...props} />;

    case "phone":
      return <OtpPhoneScreen {...props} />;

    default:
      throw new Error(`No matching screen for ${props.completion.type}`);
  }
}

const ButtonWrapper = styled.div({
  marginTop: "$spacing-32",
  marginBottom: "$spacing-32",
  display: "flex",
  flexWrap: "wrap",
});

const OTP_DIGITS = 6;

type OtpValidationState = "INITIAL" | "INVALID" | "VALID" | "VALIDATING";

const OTP_VALIDATION_STATE: { [key in OtpValidationState]: key } = {
  INITIAL: "INITIAL",
  INVALID: "INVALID",
  VALID: "VALID",
  VALIDATING: "VALIDATING",
};

function OtpPhoneScreen(props: OtpScreen) {
  const copy = useCopy();
  const isSmallScreenSize = useIsSmallScreenSize();
  const { postChangePhone } = userCompletionFormActions();

  // This is a "hack" to enable making the value bold within the copy content.
  const [stringBeforeValue, stringAfterValue] = copy(
    "completion__verify_phone_code__message",
    { 0: props.value },
  ).split(props.value);

  return (
    <Card data-test-id="completion-otp-screen">
      <Card.Title>{copy("completion__verify_phone_title")}</Card.Title>

      <Card.Text>
        {stringBeforeValue}
        <b>{props.value}</b>
        {stringAfterValue}
      </Card.Text>

      <Otp
        onSubmit={(otp) =>
          postChangePhone(props.value, otp, props.completion.context)}
        onSuccess={props.onSuccess}
      />

      <ButtonWrapper>
        <Button
          dataTestId="resend-code"
          size={isSmallScreenSize ? "small" : "medium"}
          variant="link"
          type="button"
          onClick={() =>
            // Use artificial minimum delay for improved UX
            // (no flickering button)
            minAsyncDelay(
              props.requestPhoneOtp(props.value),
              MINIMUM_SPINNER_DELAY_MS,
            )}
        >
          {copy("completion__send_new_code")}
        </Button>
      </ButtonWrapper>

      <Button
        size="medium"
        variant="secondary"
        type="button"
        onClick={() => props.onAbort()}
      >
        {copy("btn__cancel")}
      </Button>
    </Card>
  );
}

function OtpEmailScreen(props: OtpScreen) {
  const copy = useCopy();
  const [showFindCodeDialog, setShowFindCodeDialog] = useState(false);
  const isSmallScreenSize = useIsSmallScreenSize();
  const { postChangeEmail } = userCompletionFormActions();

  // This is a "hack" to enable making the value bold within the copy content.
  const [stringBeforeValue, stringAfterValue] = copy(
    "completion__verify_email_code__message",
    { 0: props.value },
  ).split(props.value);

  return (
    <>
      <Card data-test-id="completion-otp-screen">
        <Card.Title>
          {copy("completion__verify_email_code__title")}
        </Card.Title>

        <Card.Text>
          {stringBeforeValue}
          <b>{props.value}</b>
          {stringAfterValue}
        </Card.Text>

        <Otp
          onSubmit={(otp) =>
            postChangeEmail(
              props.value,
              otp,
              props.completion.context,
            )}
          onSuccess={props.onSuccess}
        />

        <ButtonWrapper>
          <Margin right={16}>
            <Button
              size={isSmallScreenSize ? "small" : "medium"}
              variant="link"
              type="button"
              onClick={() => setShowFindCodeDialog(true)}
            >
              <Button.Icon
                iconComponent={isSmallScreenSize
                  ? () => <QuestionIcon size={16} />
                  : QuestionIcon}
              />
              {copy("completion__btn__verify_email_find_code")}
            </Button>
          </Margin>

          <Button
            dataTestId="resend-code"
            size={isSmallScreenSize ? "small" : "medium"}
            variant="link"
            type="button"
            onClick={() =>
              // Use artificial minimum delay for improved UX
              // (no flickering button)
              minAsyncDelay(
                props.requestEmailOtp(props.value),
                MINIMUM_SPINNER_DELAY_MS,
              )}
          >
            {copy("completion__send_new_code")}
          </Button>
        </ButtonWrapper>

        <Button
          size="medium"
          variant="secondary"
          type="button"
          onClick={() => props.onAbort()}
        >
          {copy("btn__cancel")}
        </Button>
      </Card>

      <FindCodeDialog
        isOpen={showFindCodeDialog}
        onClose={() => setShowFindCodeDialog(false)}
      />
    </>
  );
}

const ON_SUCCESS_DELAY_MS = 1_000;

function Otp(props: {
  // FIXME: It's not optimal to use <unknown> here, but it works for now
  onSubmit(otp: string): Promise<unknown>;
  onSuccess(): void;
}) {
  const [otp, setOtp] = useState<string>();
  const [validationState, setValidationState] = useState<OtpValidationState>(
    OTP_VALIDATION_STATE.INITIAL,
  );
  const { otpState } = useSelector<RootState, OTPProps>(getOTPState);
  const theme = useTheme();
  const dispatch = useAppDispatch();

  useEffect(() => {
    async function handleOtpChanges() {
      // TODO: See if this condition could be simplified
      if (
        otp?.length === OTP_DIGITS &&
        (validationState === OTP_VALIDATION_STATE.INITIAL ||
          validationState === OTP_VALIDATION_STATE.INVALID) &&
        otpState !== "SETTING"
      ) {
        setValidationState(OTP_VALIDATION_STATE.VALIDATING);
        try {
          // Use artificial minimum delay for improved UX
          // (no flickering spinner)
          await minAsyncDelay(
            props.onSubmit(otp),
            MINIMUM_SPINNER_DELAY_MS,
          );
        } catch (otpError) {
          console.error(otpError);
          setValidationState(OTP_VALIDATION_STATE.INVALID);
        }
      } else if (otp?.length !== OTP_DIGITS) {
        // Should be cleared any time the OTP changes
        setValidationState(OTP_VALIDATION_STATE.INITIAL);
      }
    }

    handleOtpChanges();
  }, [otp]);

  useEffect(() => {
    if (otpState === "ERROR") {
      setValidationState(OTP_VALIDATION_STATE.INVALID);
    } else if (otpState === "SET") {
      setValidationState(OTP_VALIDATION_STATE.VALID);
      dispatch(clearErrorCopyKey());
      dispatch(setCompletionOTPState("INITIAL"));
      setTimeout(props.onSuccess, ON_SUCCESS_DELAY_MS);
    }
  }, [otpState]);

  return (
    <>
      <OtpWrapper>
        <OtpInput
          digits={OTP_DIGITS}
          onOtpUpdate={setOtp}
          isDisabled={validationState === OTP_VALIDATION_STATE.VALID ||
            validationState === OTP_VALIDATION_STATE.VALIDATING}
          isError={validationState === OTP_VALIDATION_STATE.INVALID}
        />
        {validationState === OTP_VALIDATION_STATE.VALIDATING && (
          <Loader size={"medium"} />
        )}
        {validationState === OTP_VALIDATION_STATE.VALID && (
          <CheckmarkCircleFilledIcon
            size={16}
            _color={theme.getColor("$active-highlight")}
          />
        )}
      </OtpWrapper>
    </>
  );
}

const OtpWrapper = styled.div({
  display: "grid",
  gridTemplateColumns: "auto auto",
  marginTop: "$spacing-32",
  alignItems: "center",
  gap: "$spacing-20",
});

const FindCodeDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const copy = useCopy();

  const Grid = styled("div")({
    display: "grid",
    gridTemplateColumns: "46px 1fr",
    gridRowGap: "$spacing-12",
  });

  return (
    <Dialog.Passive
      open={isOpen}
      onClose={onClose}
      ariaLabel="OTP"
      title={copy("completion__email_find_code_title")}
      ariaLabelCloseIcon={copy("completion__btn__email_find_code")}
      onDismissFocusRef={undefined}
    >
      <Margin bottom={32}>
        <Grid>
          <RoundedBackground
            size={32}
            backgroundColor="$surface-neutral"
          >
            <Heading size="medium" gutterBottom={false}>
              1
            </Heading>
          </RoundedBackground>
          <div>
            <Margin bottom={6}>
              <Heading size="small">
                {copy(
                  "completion__email_find_code_step1_title",
                )}
              </Heading>
            </Margin>
            <Body size="medium">
              {copy("completion__email_find_code_step1_body")}
            </Body>
          </div>
        </Grid>
        <Margin bottom={24} />
        <Grid>
          <RoundedBackground
            size={32}
            backgroundColor="$surface-neutral"
          >
            <Heading size="medium" gutterBottom={false}>
              2
            </Heading>
          </RoundedBackground>
          <div>
            <Margin bottom={6}>
              <Heading size="small">
                {copy(
                  "completion__email_find_code_step2_title",
                )}
              </Heading>
            </Margin>
            <Body size="medium">
              {copy("completion__email_find_code_step2_body")}
            </Body>
          </div>
        </Grid>
      </Margin>
      <Flex justifyContent="flex-end">
        <Button onClick={onClose}>
          {copy("completion__btn__email_find_code")}
        </Button>
      </Flex>
    </Dialog.Passive>
  );
};

interface OTPProps {
  otpState: OTPState;
}

const getOTPState = (state: RootState): OTPProps => ({
  otpState: getCompletionOTPState(state),
});

const userCompletionFormActions = () => {
  const dispatch = useAppDispatch();
  const postChangePhone = (
    phoneNumber: string,
    code: string,
    context: BECompletionV4["context"],
  ) => dispatch(requestChangePhone({ data: phoneNumber, otp: code, context }));
  const postChangeEmail = (
    email: string,
    code: string,
    context: BECompletionV4["context"],
  ) => dispatch(requestChangeEmail({ data: email, otp: code, context }));
  return { postChangePhone, postChangeEmail };
};
